import { useEffect, useState } from "react";

export const NOTEBOOK_IMG = "/images/hero-notebook.jpg";
const R = 1517 / 848;
const FONT = "560 24px Caveat";
const FLIP_EVERY = 9000;
const FLIP_MS = 900;
// [x, y, angle, maxWidth] in image pixels: right page above pen, right page below pen, then left page
const SLOTS = [[738.5, 507.7, 25.5, 190], [709.4, 522.9, 25.5, 177], [680.3, 538.1, 25.5, 166], [651.2, 553.2, 25.5, 156], [622.1, 568.4, 25.5, 145], [593.0, 583.6, 25.5, 134], [563.9, 598.8, 25.5, 123], [534.8, 614.0, 25.5, 112], [505.7, 629.1, 25.5, 101], [476.6, 644.3, 25.5, 113], [447.5, 659.5, 25.5, 135], [669.0, 707.0, 25.5, 62], [630.1, 717.5, 25.5, 80], [591.1, 728.0, 25.5, 98], [322.2, 546.4, 30.6, 157], [346.2, 536.2, 30.1, 155], [370.2, 526.0, 29.6, 153], [394.2, 515.8, 29.1, 151], [418.2, 505.6, 28.6, 149], [442.2, 495.4, 28.2, 148], [466.1, 485.2, 27.7, 146], [490.1, 475.0, 27.2, 144], [514.1, 464.8, 26.7, 142], [538.1, 454.6, 26.2, 140]];

// Splits names into notebook spreads; names wider than every slot are dropped
function paginate(names) {
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.font = FONT;
  const fits = names.filter((t) => SLOTS.some((s) => ctx.measureText(t).width <= s[3]));
  const pages = [];
  let i = 0;
  while (i < fits.length) {
    const page = [];
    for (const [x, y, a, w] of SLOTS) {
      if (i >= fits.length) break;
      if (ctx.measureText(fits[i]).width <= w) page.push({ x, y, a, t: fits[i++] });
    }
    if (!page.length) break;
    pages.push(page);
  }
  return pages;
}

export function useNotebookPages(names) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    document.fonts.load(FONT).then(() => setReady(true)).catch(() => setReady(true));
  }, []);
  const key = names.join("\u0001");
  const [pages, setPages] = useState(null);
  useEffect(() => {
    if (ready) setPages(paginate(names));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, key]);
  return pages;
}

function useFlipper(count, fixed) {
  const [page, setPage] = useState(0);
  const [turning, setTurning] = useState(false);
  useEffect(() => {
    if (fixed != null || count < 2) return;
    let t;
    const id = setInterval(() => {
      setTurning(true);
      t = setTimeout(() => { setPage((p) => (p + 1) % count); setTurning(false); }, FLIP_MS);
    }, FLIP_EVERY);
    return () => { clearInterval(id); clearTimeout(t); };
  }, [count, fixed]);
  return { page: fixed ?? Math.min(page, Math.max(0, count - 1)), turning };
}

export function Handwriting({ pages, fixedPage, highlight }) {
  const { page, turning } = useFlipper(pages.length, fixedPage);
  const lines = pages[page] || [];
  return (
    <svg viewBox="0 0 1517 848" className="absolute inset-0 w-full h-full" aria-hidden="true" data-testid="hero-notebook-names" data-page={page} data-pages={pages.length}>
      <defs><filter id="ink"><feGaussianBlur stdDeviation="0.25" /></filter></defs>
      <g key={page} className={turning ? "page-turn" : ""} fill="#1f3391" fillOpacity="0.92" filter="url(#ink)" style={{ font: FONT }}>
        {lines.map((l, i) => {
          const animate = highlight == null || l.t === highlight;
          const delay = highlight == null ? 0.4 + i * 0.45 : 0.1;
          return (
            <text key={l.t} className={animate ? "ink-write" : undefined} style={animate ? { animationDelay: `${delay}s` } : undefined}
              x={l.x} y={l.y} transform={`rotate(${l.a} ${l.x} ${l.y})`}>{l.t}</text>
          );
        })}
      </g>
      {pages.length > 1 && fixedPage == null && (
        <g data-testid="hero-notebook-pager">
          {pages.map((_, i) => <circle key={i} cx={760 + i * 16} cy={780} r={4.5} fill="#1f3391" fillOpacity={i === page ? 0.75 : 0.2} />)}
        </g>
      )}
    </svg>
  );
}

export function HeroBackdrop({ src, names, pages: given, fixedPage, highlight }) {
  const own = useNotebookPages(given ? [] : names || []);
  const pages = given || own;
  const W = `max(100cqw, 100cqh * ${R})`;
  const H = `max(100cqh, 100cqw / ${R})`;
  if (src) {
    return <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-[35%_62%]" />;
  }
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ containerType: "size" }}>
      <div className="absolute" style={{ width: W, height: H, left: `calc((100cqw - ${W}) * 0.35)`, top: `calc((100cqh - ${H}) * 0.62)` }}>
        <img src={NOTEBOOK_IMG} alt="" aria-hidden="true" fetchpriority="high" className="w-full h-full" />
        {pages && <Handwriting pages={pages} fixedPage={fixedPage} highlight={highlight} />}
      </div>
    </div>
  );
}
